import React, { useEffect, useMemo, useState } from "react";

import CalendarHeatmap from "components/dashboard/calendar-heatmap/CalendarHeatmap";
import {
  AnalyticsResultDto,
  AnyFilterExpressionInput,
  BooleanOp,
  FilterOp,
  HeatData,
  MdmObjectDto,
  SortDirection,
  SortInput,
  useGetAnalyticsByObjectIdPagedLazyQuery,
  useGetAnalyticsByProjectIdPagedLazyQuery,
  useGetAnalyticsHeatmapByObjectIdLazyQuery,
  useGetAnalyticsHeatmapByProjectIdLazyQuery,
  useUpdateTableDetailsForUserMutation
} from "gql/generated/types";
import { ProgressSpinner } from "primereact/progressspinner";
import { Link } from "react-router-dom";
import { DEFAULT_DATE_FORMATTER } from "utils/constants";
import { getFirstDayOfHalfYear, getLastDayOfCurrentMonthDate } from "utils/joda-time-utils";
import { parseToLocalZoneDateTime } from "utils/time-utils";
import useProjectContext from "../hooks/useProjectContext";
import useTableFilter, { initialFilter } from "../utils/hooks/useTableFilter";
import { TableOptions, TableColumn, MdmTableName } from "../utils/types";
import { errorToaster, renderSeverity, successToaster, userTableDetails } from "../utils/utils";
import LazyTable from "./LazyTable";
import TableColumnSelection from "./TableColumnSelection";

type ObjectAnalyticsProps = {
  object?: MdmObjectDto;
  tableName: MdmTableName;
};

const ObjectAnalytics: React.FC<ObjectAnalyticsProps> = ({ object, tableName }) => {
  const { projectId, userSettings, project } = useProjectContext();
  const [dateFrom, setDateFrom] = React.useState<Date>(getFirstDayOfHalfYear());
  const [dateTo, setDateTo] = React.useState<Date>(getLastDayOfCurrentMonthDate());
  const [customColumns, setCustomColumns] = useState<string[]>([]);

  const tableDetails = userTableDetails(userSettings, tableName);
  const [updateUserSettings, { loading: updateLoading }] = useUpdateTableDetailsForUserMutation({
    onCompleted: () => successToaster("Columns saved successfully"),
    onError: errorToaster,
  });

  const [tableOptions, setTableOptions] = useTableFilter(tableName, {
    sortField: "id.date",
    reqSortField: "id.date",
    sortOrder: -1,
  });

  const [getAnalyticsByObject, {data: analytics, loading}] = useGetAnalyticsByObjectIdPagedLazyQuery({
    onError: err => errorToaster(err)
  })

  const [getAnalyticsByProject, {data, loading: loading2 }] = useGetAnalyticsByProjectIdPagedLazyQuery({
    onError: err => errorToaster(err)
  })

  const getFilteredData = tableName === "Analytics" ? getAnalyticsByProject : getAnalyticsByObject
  const totalAnalytics =
    analytics?.allAnalyticsResultsByObjectIdAndDateRangePaged.pageInfo?.total ??
    data?.allAnalyticsResultsByProjectIdAndDateRangePaged.pageInfo?.total ??
    0;
  const isLoading = loading || loading2;
  const analyticsData = useMemo(
    () =>
      (analytics?.allAnalyticsResultsByObjectIdAndDateRangePaged.items ??
        data?.allAnalyticsResultsByProjectIdAndDateRangePaged.items ??
        []) as AnalyticsResultDto[],
    [analytics, data]
  );

  useEffect(() => {
    if (!isLoading && userSettings) {
      fetchData(tableDetails?.rows);
    }
    const columnList = userTableDetails(userSettings, tableName)?.columnOrders?.map(r => r!.name) ?? [];

    if (userSettings && columnList.length > 0) {
      setCustomColumns(columnList);
    } else {
      setCustomColumns(columns.map(col => col.header));
    }
  }, [userSettings, dateTo, dateFrom]);

  const fetchData = async (
    take: number | null = null,
    skip: number | null = null,
    sort: SortInput[] | null = null,
    filters: AnyFilterExpressionInput[] | null = null,
    cacheAndNetwork: boolean = false,
    filter: AnyFilterExpressionInput | null = null,
  ) => {
    const localStorageFilters = localStorage.getItem(`${projectId}-${tableName}`);
    const storageFilters: TableOptions = JSON.parse(localStorageFilters ?? "{}");
    const resp = await getFilteredData({
      variables: {
        input: {
          id: object?.id ?? projectId,
          range: {
            start: dateFrom,
            startInclusive: true,
            end: dateTo,
            endInclusive: true,
          },
          take: take ?? storageFilters?.rows ?? tableOptions.rows,
          skip: skip ?? storageFilters?.first ?? tableOptions.first,
          sort: sort ?? [{
            property: storageFilters?.reqSortField ?? tableOptions.reqSortField,
            direction: (storageFilters?.sortOrder || tableOptions.sortOrder) === 1 ? SortDirection.Asc : SortDirection.Desc,
          }],
          andFilters: filters ?? storageFilters?.andFilters ?? tableOptions.andFilters,
          filter: filter ?? storageFilters?.filter ?? tableOptions.filter,
        },
      },
      fetchPolicy: cacheAndNetwork ? "cache-and-network" : "cache-first",
    });
    return resp.data;
  };

  const handleSearch = (value: string) => {
    const searchFilter: AnyFilterExpressionInput | null = {
      op: BooleanOp.Or,
      left: {
        op: BooleanOp.Or,
        left: {
          op: BooleanOp.Or,
          left: {
            value,
            cond: FilterOp.Contains,
            property: "description",
          },
          right: {
            value,
            cond: FilterOp.Contains,
            property: "mdmObject.address.fullAddress",
          },
        },
        right: {
          op: BooleanOp.Or,
          left: {
            value,
            cond: FilterOp.Contains,
            property: "device.identifier",
          },
          right: {
            value,
            cond: FilterOp.Contains,
            property: "mdmObject.identifier",
          },
        },
      },
      right: {
        op: BooleanOp.Or,
        left: {
          value,
          cond: FilterOp.Contains,
          property: "deviceType",
        },
        right: {
          value,
          cond: FilterOp.Contains,
          property: "severity",
        },
      },
    };
    setTableOptions(prev => ({
      ...prev,
      filter: value ? searchFilter : null,
      first: 0,
      search: value,
    }));
    fetchData(null, 0, null, null, false, searchFilter);
  };
  
  const renderObjectLink = (rowData: AnalyticsResultDto) => (
    <Link
      to={`/mdm/${projectId}/objects/${rowData.mdmObject?.id}`}
      className="truncate-text block"
      title={rowData.mdmObject?.title ?? ""}
    >
      {rowData.mdmObject?.title}
    </Link>
  );

  const renderDeviceLink = (rowData: AnalyticsResultDto) => (
    <Link to={`/mdm/${projectId}/devices/${rowData.mdmDevice?.id}`}>{rowData.mdmDevice?.identifier}</Link>
  );

  const customCols =
    tableName === "Analytics"
      ? [
          ...(project?.mdmProjectById.configuration?.objectConfiguration.customFields ?? []),
          ...(project?.mdmProjectById.configuration?.deviceConfiguration.customFields ?? []),
        ]
      : [...(project?.mdmProjectById.configuration?.deviceConfiguration.customFields ?? [])];

  const columns: TableColumn<AnalyticsResultDto>[] = [
    {
      field: "id.date",
      header: "Date",
      sortable: true,
      body: rowData => parseToLocalZoneDateTime(rowData.id?.date).format(DEFAULT_DATE_FORMATTER),
      columnKey: "date",
    },
    {
      field: "severity",
      header: "Severity",
      body: renderSeverity,
      sortable: true,
      columnKey: "severity",
    },
    {
      field: "id.analyticsType",
      header: "Analytic Type",
      sortable: true,
      columnKey: "analyticType",
    },
    { field: "description", header: "Description", sortable: true, columnKey: "description" },
    {
      field: "deviceType",
      header: "Device Type",
      sortable: true,
      columnKey: "deviceType",
    },
    {
      field: "mdmDevice.identifier",
      header: "Device",
      body: renderDeviceLink,
      columnKey: "device",
    },
    {
      field: "mdmObject.title",
      header: "Object",
      body: renderObjectLink,
      columnKey: "object",
      sortable: true,
      hidden: !!object,
    }
  ];

  const mapCols = [
    ...columns,
    ...(customCols?.map(col => ({
      field: `${col?.id}.value`,
      header: col?.title ?? "",
      sortable: true,
      columnKey: col?.id,
    })) ?? []),
  ]

  const formatObjectFields = analyticsData.reduce((acc, anal) => {
    const customFields =
      tableName === "Analytics"
        ? [...(anal.mdmObject?.customFields ?? []), ...(anal.mdmDevice?.customFields ?? [])]
        : anal.mdmDevice?.customFields;
    const objFields = customFields?.reduce((accObj, field) => {
      const newObj = {
        value: field?.value?.value,
        id: field?.key,
        title: customCols.find(col => col?.id === field?.key)?.title,
      };
      return {
        ...accObj,
        [field?.key!]: newObj,
      };
    }, {});

    acc.push({
      ...anal,
      ...objFields,
    });

    return acc;
  }, [] as any[]);

  const filteredColumns = mapCols.filter(col => customColumns.includes(col.header));

  const handleColumnSave = () => {
    updateUserSettings({
      variables: {
        tableDetails: {
          table: tableName,
          rows: tableOptions.rows,
          columnOrders: customColumns.map((col, i) => ({
            name: col,
            order: i,
          })),
        },
      },
    });
  };

  const resetFilters = () => {
    setTableOptions(prev => ({
      ...initialFilter,
      rows: prev.rows,
      sortField: "id.date",
      reqSortField: "id.date",
      sortOrder: -1,
    }));
    fetchData(
      tableOptions.rows,
      0,
      [{ direction: SortDirection.Desc, property: "id.date" }],
      [],
      false,
      { cond: FilterOp.Contains, property: "description", value: "" }
    );
  };

  return (
    <div className="mt-4">
      <CalendarHeatmapWrapper
        tableName={tableName}
        queryId={object?.id ?? projectId!}
        dateFrom={dateFrom}
        dateTo={dateTo}
        setDateFrom={setDateFrom}
        setDateTo={setDateTo}
      />
      <LazyTable
        columns={filteredColumns}
        data={formatObjectFields}
        tableName={tableName}
        buttons={
          <TableColumnSelection
            customColumns={customColumns}
            setCustomColumns={setCustomColumns}
            handleColumnSave={handleColumnSave}
            mapCol={mapCols}
            loading={updateLoading}
          />
        }
        containerName="AnalyticsResult"
        loading={isLoading}
        setTableOptions={setTableOptions}
        resetFilters={resetFilters}
        tableOptions={tableOptions}
        totalItems={totalAnalytics}
        fetchData={fetchData}
        onSearch={handleSearch}
      />
    </div>
  );
}

export default ObjectAnalytics;

type CalendarHeatmapWrapperProps = {
  tableName: MdmTableName;
  queryId: string;
  dateFrom: Date;
  dateTo: Date;
  setDateFrom: React.Dispatch<React.SetStateAction<Date>>;
  setDateTo: React.Dispatch<React.SetStateAction<Date>>;
}

const CalendarHeatmapWrapper: React.FC<CalendarHeatmapWrapperProps> = ({
  tableName,
  queryId,
  dateFrom,
  dateTo,
  setDateFrom,
  setDateTo,
}) => {
  const [getHeatmapByObject, { data: objectHeatmap, loading }] =
    useGetAnalyticsHeatmapByObjectIdLazyQuery({
      onError: err => errorToaster(err),
    });

  const [getHeatmapByProject, { data: projectHeatmap, loading: loading2 }] =
    useGetAnalyticsHeatmapByProjectIdLazyQuery({
      onError: err => errorToaster(err),
    });

  const isLoading = loading || loading2;

  const getHeatmapData =
    tableName === "Analytics" ? getHeatmapByProject : getHeatmapByObject;

  const analyticsData = useMemo(
    () =>
      (objectHeatmap?.analyticsHeatmapByObjectIdAndDateRange ??
        projectHeatmap?.analyticsHeatmapByProjectIdAndDateRange ??
        []) as HeatData[],
    [objectHeatmap, projectHeatmap]
  );

  useEffect(() => {
    getHeatmapData({
      variables: {
        id: queryId,
        range: {
          start: dateFrom,
          startInclusive: true,
          end: dateTo,
          endInclusive: true,
        },
      },
    });
  }, [dateFrom, dateTo]);

  if (isLoading) return <ProgressSpinner/>

  const formatHeatData = analyticsData.map(data => ({
    value: data.value,
    date: new Date(data.date),
  }));

  return (
    <CalendarHeatmap
      startDate={dateFrom}
      endDate={dateTo}
      dataset={formatHeatData}
      setStartDate={setDateFrom}
      setEndDate={setDateTo}
      highValueColor="ed0000"
      lowValueColor="fde3e3"
      minValue={0}
      maxValue={100}
      staticMinMaxColors
      staticMinMaxTitles={false}
      staticMinMaxValues
      isCustomDataset
      showDateSelection
    />
  );
};